import type { ApiClient, WsClient } from "@agentsaas/client-sdk";
import { encodeTerminalText, terminalDecoder } from "@agentsaas/client-sdk";
import type { Channel } from "@agentsaas/protocol";
import { isEvent } from "../data/realtime";

/**
 * 终端会话（与 xterm / React 无关）：
 * terminalOpen → 订阅 terminal:<tid>（输出走 WS 二进制帧）→ 输入 / resize / close 走 cmd。
 */
export interface TerminalSink {
  write(text: string): void;
  exit(code: number): void;
  /** 通道订阅完成，可以收发 */
  ready?(): void;
}

/** pty 就绪前最多缓冲的输入字符数（超出丢弃最早的） */
const MAX_BUFFER = 64 * 1024;

export class TerminalSession {
  terminalId: string | null = null;
  private unsub: (() => void) | null = null;
  private closed = false;
  private decode = terminalDecoder();
  /** 通道订阅完成（pty 就绪）之前的输入先缓冲，就绪后按序发送 */
  private ready = false;
  private buffer: string[] = [];
  private buffered = 0;
  private pendingSize: { cols: number; rows: number } | null = null;

  constructor(
    private readonly api: ApiClient,
    private readonly ws: WsClient,
    private readonly aid: string,
    private readonly sink: TerminalSink,
  ) {}

  get channel(): Channel | null {
    return this.terminalId ? (`terminal:${this.terminalId}` as Channel) : null;
  }

  async open(cols: number, rows: number) {
    const { terminalId } = await this.api.call("terminalOpen", { params: { aid: this.aid }, body: { cols: Math.max(1, cols), rows: Math.max(1, rows) } });
    this.terminalId = terminalId;
    if (this.closed) {
      void this.ws.cmd(this.channel!, "close").catch(() => {});
      return;
    }
    this.unsub = this.ws.subscribe(this.channel!, {
      onBinary: (bytes) => this.sink.write(this.decode(bytes)),
      onSubscribed: () => {
        if (!this.ready) {
          this.ready = true;
          this.flush();
        }
        this.sink.ready?.();
      },
      onEvent: (e) => {
        if (isEvent(e, "terminal.exit")) this.sink.exit(e.data.code);
      },
    });
  }

  input(text: string) {
    if (this.closed || !text) return;
    if (!this.ready || !this.channel) {
      this.buffer.push(text);
      this.buffered += text.length;
      while (this.buffered > MAX_BUFFER && this.buffer.length > 1) this.buffered -= this.buffer.shift()!.length;
      return;
    }
    void this.ws.cmd(this.channel, "input", { text }).catch(() => {});
  }

  resize(cols: number, rows: number) {
    if (cols < 1 || rows < 1) return;
    if (!this.ready || !this.channel) {
      this.pendingSize = { cols, rows };
      return;
    }
    void this.ws.cmd(this.channel, "resize", { cols, rows }).catch(() => {});
  }

  /** 就绪时：先同步尺寸，再把缓冲的输入合并成一条发送（保持顺序） */
  private flush() {
    if (this.pendingSize) {
      const { cols, rows } = this.pendingSize;
      this.pendingSize = null;
      this.resize(cols, rows);
    }
    if (this.buffer.length) {
      const text = this.buffer.join("");
      this.buffer = [];
      this.buffered = 0;
      this.input(text);
    }
  }

  close() {
    if (this.closed) return;
    this.closed = true;
    if (this.channel) void this.ws.cmd(this.channel, "close").catch(() => {});
    this.unsub?.();
    this.unsub = null;
  }
}

/** 手机快捷键 → 终端控制序列 */
export const KEY_SEQ = {
  tab: "\t",
  esc: "\x1b",
  up: "\x1b[A",
  down: "\x1b[B",
  right: "\x1b[C",
  left: "\x1b[D",
} as const;

/** Ctrl + 字符 → 控制字符（Ctrl+C = \x03） */
export function ctrlChar(ch: string): string {
  const c = ch.toUpperCase().charCodeAt(0);
  return c >= 64 && c <= 95 ? String.fromCharCode(c & 0x1f) : ch;
}

export { encodeTerminalText };
